// =====================================================================================
// FILE: src/components/auth/AuthGate.jsx
// Beta security gate - URL key or password required
// =====================================================================================
import React, { useState, useEffect } from "react";
import { THEME, FONTS } from "../../constants/theme";

// ✅ CONFIGURATION - Change these values as needed
const VALID_KEY = "MatchFlow2026Beta";  // URL key parameter
const VALID_PASSWORD = "MatchFlow2026"; // Password for manual entry
const STORAGE_KEY = "venueflow_auth";

// ✅ SET TO TRUE TO DISABLE AUTH GATE (bypass password screen)
const DISABLE_AUTH = true;

export default function AuthGate({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(DISABLE_AUTH);
  const [isChecking, setIsChecking] = useState(!DISABLE_AUTH);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    // If auth is disabled, skip all checks
    if (DISABLE_AUTH) {
      setIsAuthenticated(true);
      setIsChecking(false);
      return;
    }

    // Check authentication on mount
    const checkAuth = () => {
      // 1. Check URL parameter
      const params = new URLSearchParams(window.location.search);
      const urlKey = params.get("key");
      
      if (urlKey === VALID_KEY) {
        // Valid URL key - store and grant access
        sessionStorage.setItem(STORAGE_KEY, "true");
        setIsAuthenticated(true);
        setIsChecking(false);
        return;
      }

      // 2. Check sessionStorage (already authenticated this session)
      if (sessionStorage.getItem(STORAGE_KEY) === "true") {
        setIsAuthenticated(true);
        setIsChecking(false);
        return;
      }

      // 3. Not authenticated - show password screen
      setIsChecking(false);
    };

    checkAuth();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (password === VALID_PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, "true");
      setIsAuthenticated(true);
      setError("");
    } else {
      setError("Invalid password. Please try again.");
      setPassword("");
    }
  };

  // Still checking auth status
  if (isChecking) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>Loading...</div>
        </div>
      </div>
    );
  }

  // Not authenticated - show password screen
  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Logo/Header */}
          <div style={styles.logoContainer}>
            <div style={styles.logo}>L8</div>
            <div style={styles.title}>Lumen8 VenueFlow</div>
            <div style={styles.subtitle}>MatchFlow Beta Access</div>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Enter Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Beta access password"
                style={styles.input}
                autoFocus
              />
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button type="submit" style={styles.button}>
              Access MatchFlow
            </button>
          </form>

          <div style={styles.footer}>
            Contact your Lumen8 coordinator for access
          </div>
        </div>
      </div>
    );
  }

  // Authenticated - render the app
  return children;
}

// =====================================================================================
// STYLES
// =====================================================================================
const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(135deg, ${THEME.dark} 0%, #1a1a2e 100%)`,
    padding: 20,
  },
  card: {
    background: THEME.surface,
    borderRadius: 16,
    padding: 40,
    width: "100%",
    maxWidth: 400,
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
  },
  logoContainer: {
    textAlign: "center",
    marginBottom: 32,
  },
  logo: {
    width: 60,
    height: 60,
    background: THEME.primary,
    borderRadius: 12,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    fontWeight: 700,
    color: THEME.dark,
    fontFamily: FONTS.family,
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: THEME.dark,
    fontFamily: FONTS.family,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: THEME.textSecondary,
    fontFamily: FONTS.family,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: THEME.text,
    fontFamily: FONTS.family,
  },
  input: {
    padding: "14px 16px",
    fontSize: 16,
    border: `2px solid ${THEME.line}`,
    borderRadius: 10,
    outline: "none",
    fontFamily: FONTS.family,
    transition: "border-color 0.2s ease",
    background: THEME.surface,
  },
  button: {
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 600,
    background: THEME.primary,
    color: THEME.dark,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: FONTS.family,
    marginTop: 8,
    transition: "transform 0.1s ease, box-shadow 0.2s ease",
  },
  error: {
    color: "#e53e3e",
    fontSize: 14,
    textAlign: "center",
    fontFamily: FONTS.family,
    padding: "8px 12px",
    background: "#fde2e2",
    borderRadius: 8,
  },
  footer: {
    marginTop: 24,
    textAlign: "center",
    fontSize: 13,
    color: THEME.textMuted,
    fontFamily: FONTS.family,
  },
  loading: {
    textAlign: "center",
    padding: 40,
    fontSize: 16,
    color: THEME.textSecondary,
    fontFamily: FONTS.family,
  },
};
